import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

export interface ScrollableRowRef {
	scrollLeft: () => void;
	scrollRight: () => void;
	canScrollLeft: boolean;
	canScrollRight: boolean;
}

interface ScrollableRowProps {
	children: React.ReactNode;
	className?: string;
	onScrollStateChange?: (canScrollLeft: boolean, canScrollRight: boolean) => void;
	ref?: React.Ref<ScrollableRowRef>;
}

function ScrollableRow({ children, className, onScrollStateChange, ref }: ScrollableRowProps) {
	const scrollRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const checkScrollability = useCallback(() => {
		if (!scrollRef.current) {
			return;
		}

		const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
		const newCanScrollLeft = scrollLeft > 0;
		const newCanScrollRight = scrollLeft < scrollWidth - clientWidth - 1;

		setCanScrollLeft(newCanScrollLeft);
		setCanScrollRight(newCanScrollRight);

		// Notify parent component of scroll state changes
		onScrollStateChange?.(newCanScrollLeft, newCanScrollRight);
	}, [onScrollStateChange]);

	const scrollLeft = useCallback(() => {
		if (!scrollRef.current) {
			return;
		}
		scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
	}, []);

	const scrollRight = useCallback(() => {
		if (!scrollRef.current) {
			return;
		}
		scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
	}, []);

	// Expose methods and state to parent component via ref
	useEffect(() => {
		if (ref && typeof ref === 'object' && ref !== null) {
			(ref as React.MutableRefObject<ScrollableRowRef>).current = {
				scrollLeft,
				scrollRight,
				canScrollLeft,
				canScrollRight,
			};
		}
	}, [ref, scrollLeft, scrollRight, canScrollLeft, canScrollRight]);

	// Check scrollability on mount and when children change
	useEffect(() => {
		checkScrollability();
		// Use a small delay to ensure content is rendered
		const timeoutId = setTimeout(checkScrollability, 100);
		return () => clearTimeout(timeoutId);
	}, [checkScrollability]);

	return (
		<div
			ref={scrollRef}
			className={cn(
				'flex gap-1 overflow-x-auto pe-4 pb-2 sm:gap-1.5',
				'[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
				className,
			)}
			style={{
				msOverflowStyle: 'none',
				WebkitOverflowScrolling: 'touch',
			}}
			onScroll={checkScrollability}
			onLoad={checkScrollability}
		>
			{children}
		</div>
	);
}

export { ScrollableRow };
